// Provider-aware auth facade (C-2 dashboard login cutover).
//
// One place that decides "Kinde or Firebase?" so every seam — superFetch, the org/
// profile bootstrap sagas, checkAuthorization, logout — asks the same question
// instead of each re-deriving it. When the Kinde flag is off (or there is no live
// Kinde session) every function falls through to the EXISTING Firebase behaviour,
// byte-for-byte. That keeps rollback to a single flag flip.

import { jwtDecode } from 'jwt-decode';
import { getCurrentUser, signOut } from 'library/firebase/fb.auth.utils';
import {
	getKindeClient,
	getKindeClientId,
	isKindeEnabled,
	login as kindeLogin,
	resetKindeClient,
	tokenAzpMatches
} from 'library/kinde/kindeClient';

// getActiveKindeClient() and the token fetch below sit on the hot path — getAuthToken() (called
// by every superFetch request) awaits both every time. Un-memoized, a page that fires N concurrent
// requests on mount (e.g. the Day Passes admin page, ~8 calls) triggered N independent
// client.isAuthenticated() calls when the access token was expired, each doing its OWN
// refresh-token exchange against the SAME refresh token. Kinde ROTATES refresh tokens on every
// use and invalidates the old one immediately (with only a brief grace window for a retry) —
// Kinde's own docs: reusing an already-rotated refresh token trips automatic reuse detection and
// invalidates ALL of that session's tokens. So N concurrent refresh attempts racing on one refresh
// token didn't just waste time — it plausibly caused the reported CORS/405 failures directly: the
// requests that lost the race hit an already-rotated token, got rejected, and (if that error
// response lacks CORS headers, as many APIs' error paths do) Chrome surfaces that as a CORS error
// rather than the real status, while sibling requests show up as 404/405 in the network tab.
// Two-layer guard, applied to BOTH the session check and the token fetch below:
//   (1) in-flight de-dup: while a check/fetch is running, every concurrent caller shares that one
//       promise — regardless of outcome. This alone collapses a same-tick burst of N requests
//       into ONE Kinde call, which is what actually prevents the N-way refresh-token race.
//   (2) positive-only TTL: once resolved, keep the promise for a short TTL ONLY if it resolved to
//       a truthy result. A `null`/failure is NOT held — so a flaky token endpoint doesn't poison
//       every caller for the whole window (the next call re-checks and can recover).
// forceRefreshToken()/logout() drop both caches explicitly for their own paths. Accepted tradeoff:
// caching the token STRING for the TTL means a caller in that window skips the SDK's own per-call
// expiry check, so a token whose real remaining lifetime is shorter than CACHE_TTL_MS could be
// served past its expiry. Kinde access tokens run minutes-to-an-hour, far above this window, so
// this is a deliberate, low-probability tradeoff for eliminating the N-way refresh race — not an
// oversight.
const CACHE_TTL_MS = 5000;
let inFlightCheck = null; // shared promise while a session check is running (any outcome)
let cachedClient = null; // { client: KindeClient, expires: number } — POSITIVE results only
let inFlightToken = null; // shared promise while a token fetch is running (any outcome)
let cachedToken = null; // { token: string, expires: number } — POSITIVE (truthy) results only

/**
 * The Kinde client IFF the flag is on AND there is a live Kinde session; else null.
 * Never throws — any failure resolves null so callers fall back to Firebase.
 */
const getActiveKindeClient = () => {
	const now = Date.now();
	if (cachedClient && cachedClient.expires > now) return Promise.resolve(cachedClient.client);
	if (inFlightCheck) return inFlightCheck;

	// Capture this call's own promise so its cleanup can check "am I still the current one?"
	// before touching shared state — an invalidation (forceRefreshToken/logout) that fires while
	// THIS check is in flight starts a fresh inFlightCheck; if the old one resolves after that, it
	// must not clobber the newer cachedClient/inFlightCheck with its now-stale result.
	const thisCheck = (async () => {
		if (!isKindeEnabled()) return null;
		try {
			const client = await getKindeClient();
			if (!client) return null;
			const authed = await client.isAuthenticated();
			return authed ? client : null;
		} catch {
			return null;
		}
	})().then((client) => {
		if (inFlightCheck === thisCheck) {
			if (client) cachedClient = { client, expires: Date.now() + CACHE_TTL_MS };
			inFlightCheck = null;
		}
		return client;
	});
	inFlightCheck = thisCheck;
	return thisCheck;
};

/**
 * The current Kinde bearer token, de-duped/cached the same way as getActiveKindeClient — this is
 * the second half of the hot path. getToken() (legacy reader) has its OWN independent
 * refresh-token exchange (a different code path/storage than isAuthenticated's, per the SDK's
 * dual-storage-system quirk — see the getAuthToken comment below), so without this it would still
 * race N-wide even with the session check memoized above.
 */
const getKindeToken = (client) => {
	const now = Date.now();
	if (cachedToken && cachedToken.expires > now) return Promise.resolve(cachedToken.token);
	if (inFlightToken) return inFlightToken;

	// Same "am I still current?" identity guard as getActiveKindeClient — see its comment. Without
	// it, a fetch started before forceRefreshToken() invalidates could resolve afterward and
	// silently overwrite the fresh token forceRefreshToken's caller is relying on, or null out a
	// newer in-flight fetch's tracking (re-opening the N-way race this fix exists to close).
	const thisFetch = (async () => {
		let token;
		try {
			// 🛑 THE ORDER IS LOAD-BEARING. Do NOT "modernise" this to `getAccessToken()` alone,
			// however loudly the SDK's deprecation notice asks for it. Measured against
			// @kinde-oss/kinde-auth-pkce-js 4.5.0 — the two are NOT interchangeable:
			//
			//   getToken()       reads the legacy token_bundle, CHECKS EXPIRY, and performs a
			//                    refresh_token exchange against the token endpoint when stale. It is
			//                    the only public token getter that refreshes.
			//   getAccessToken() is a pure read of the new session store (`getSessionItem`) with no
			//                    expiry check — it will happily hand back an EXPIRED token.
			//
			// Swapping to getAccessToken() removes silent renewal from every API call, so sessions
			// would start 401ing roughly an hour in — bringing forward the exact outage the
			// deprecation warning is warning about. The per-call console noise is the price of
			// refresh until the migration is done deliberately. (`isAuthenticated()` is
			// `useRefreshToken: true`, so `await isAuthenticated()` then `getAccessToken()` is the
			// likely path — but it has to be reconciled with the token cache below, not swapped in.)
			//
			// The typeof guard is for the day Kinde actually removes getToken(): calling a missing
			// method throws a TypeError that the catch below swallows, which would skip
			// getAccessToken() entirely and return undefined for EVERY request. This degrades to a
			// read-only (unrefreshed) token instead of to no token at all.
			const legacyToken = typeof client.getToken === 'function' ? await client.getToken() : undefined;
			token = legacyToken || (await client.getAccessToken()) || undefined;
		} catch {
			return undefined;
		}
		// Reject a token minted for another Kinde client on the shared auth.zezamii.com SSO cookie.
		// Checked HERE, before the .then() below can cache it: caching a foreign token would keep
		// serving 401s for the whole TTL, and the 401 recovery path would force a refresh that
		// reproduces the same token. Returning undefined also keeps it out of the cache for free —
		// only truthy results are stored.
		if (token && !tokenAzpMatches(token, getKindeClientId())) {
			await discardForeignKindeSession();
			return undefined;
		}
		if (token) setRecoveryMark(false); // a good token means any earlier recovery worked
		return token;
	})().then((token) => {
		if (inFlightToken === thisFetch) {
			if (token) cachedToken = { token, expires: Date.now() + CACHE_TTL_MS };
			inFlightToken = null;
		}
		return token;
	});
	inFlightToken = thisFetch;
	return thisFetch;
};

/** Drop both caches so the next call re-verifies/re-fetches immediately. */
const invalidateActiveClientCache = () => {
	cachedClient = null;
	inFlightCheck = null;
	cachedToken = null;
	inFlightToken = null;
};

/** Test-only: reset the memoized session check + token fetch between test cases. Not used by app code. */
export const __resetAuthProviderCacheForTests = invalidateActiveClientCache;

// One-shot marker for the foreign-session recovery below. sessionStorage (not localStorage) so it
// dies with the tab: a user who hits this once, recovers, and returns tomorrow gets a fresh
// attempt rather than being permanently barred from the automatic fix.
const AZP_RECOVERY_KEY = 'kinde_azp_recovery';

const readRecoveryMark = () => {
	try {
		return sessionStorage.getItem(AZP_RECOVERY_KEY) === '1';
	} catch {
		// sessionStorage can throw in private mode / partitioned contexts. Report "already tried"
		// so we take the NON-redirecting branch — without durable state we cannot bound a redirect
		// loop, and a signed-out app is recoverable by hand while an infinite redirect is not.
		return true;
	}
};

const setRecoveryMark = (on) => {
	try {
		if (on) sessionStorage.setItem(AZP_RECOVERY_KEY, '1');
		else sessionStorage.removeItem(AZP_RECOVERY_KEY);
	} catch {
		/* best-effort — readRecoveryMark already fails safe when storage is unavailable */
	}
};

/**
 * Recover from holding a token minted for a DIFFERENT Kinde client (see `tokenAzpMatches`).
 *
 * The token is unusable and re-refreshing reproduces it, so the only exit is a fresh `/authorize`
 * round-trip: that carries OUR client_id, so Kinde re-mints against the same SSO session and the
 * user is not asked to re-enter credentials. Discarding first matters because the SDK would
 * otherwise resurrect the foreign refresh token — `migrateLegacyRefreshTokenKey` reads the raw
 * localStorage key back into a fresh session on the next page load (the same hazard `logout`
 * documents). The in-memory half of the SDK store needs no explicit clearing: the redirect
 * unloads the page and takes it with it.
 *
 * Redirects AT MOST ONCE per tab. If the mark is already set the mismatch survived a full
 * re-auth, which means something structural (e.g. Kinde app config on the shared domain) that a
 * second redirect cannot fix — so we degrade to signed-out, which is inconvenient but finite.
 * That bound is the whole point: the bug being fixed is an infinite loop.
 */
const discardForeignKindeSession = async () => {
	localStorage.removeItem(KINDE_LEGACY_REFRESH_TOKEN_KEY);
	invalidateActiveClientCache();
	resetKindeClient();

	if (readRecoveryMark()) return;
	setRecoveryMark(true);
	try {
		await kindeLogin(); // full-page redirect; also drops the SDK's in-memory store
	} catch {
		// best-effort — the caller has already returned no token, so the app shows signed-out
	}
};

/**
 * True when the current session is a Kinde session. Used by checkAuthorization to
 * branch WITHOUT disturbing the Firebase-only restore path (a Firebase session
 * returns false here and takes the unchanged localStorage/expiry path).
 */
export const isKindeSession = async () => (await getActiveKindeClient()) !== null;

/**
 * Provider-aware bearer token for SuperFetch.
 * - Kinde session → the access token (silently refreshed; the backend verifies the
 *   access token and fetches the verified email from userinfo).
 * - Otherwise → the existing Firebase id-token path, unchanged.
 * Returns undefined when there is no token (caller then sends no Authorization header).
 */
export const getAuthToken = async () => {
	const kinde = await getActiveKindeClient();
	if (kinde) {
		// getKindeToken (memoized, see above) tries getToken() first — the reliable reader for
		// the legacy (is_dangerously_use_local_storage) store this client actually persists
		// into — falling back to getAccessToken() (the newer session-store reader) so the
		// Bearer token attaches regardless of which store the SDK populated.
		return await getKindeToken(kinde);
	}

	// --- Firebase path (unchanged from the original superFetch.getToken) ---
	const user = await getCurrentUser();
	if (!user) return undefined;
	let token = await user.getIdToken();
	const profile = jwtDecode(token);
	const expiredAt = profile.expiredAt || profile.exp * 1000;
	if (expiredAt < new Date().getTime() - 10000) {
		token = await user.getIdToken(true);
		localStorage.setItem('id_token', token);
	}
	return token;
};

/**
 * Force a fresh access/id token (provider-aware). Used to recover a login-bootstrap call that
 * raced a not-yet-fresh cached token and got a 401 — force a renewal so the immediate retry
 * carries a valid token, sparing the user a manual hard refresh. Best-effort, never throws.
 */
export const forceRefreshToken = async () => {
	// Force a fresh session check rather than trusting a cached result from moments ago — the
	// caller reached here BECAUSE a request just 401'd, so a cached "authenticated" (now-stale)
	// or "unauthenticated" (transient failure) entry must not be reused for this recovery attempt.
	invalidateActiveClientCache();
	try {
		const kinde = await getActiveKindeClient();
		if (kinde) {
			await kinde.getToken({ isForceRefresh: true }); // refresh-token exchange → renews the stored token
			return;
		}
		const user = await getCurrentUser();
		if (user) {
			const token = await user.getIdToken(true); // Firebase force-refresh
			localStorage.setItem('id_token', token);
		}
	} catch {
		// best-effort — the caller retries regardless and surfaces the error if it still fails
	}
};

/**
 * Is there any authenticated session (either provider)? Used by the profile
 * bootstrap null-check, which previously keyed off Firebase getCurrentUser().
 */
export const isAuthenticated = async () => {
	if (await getActiveKindeClient()) return true;
	return Boolean(await getCurrentUser());
};

/**
 * Stable provider user id — Kinde `sub` or Firebase `uid`. Only used to populate the
 * orgByPerson query param for parity/logging; the backend derives the authoritative
 * actor from the verified token, so this value is advisory (and ignored server-side).
 */
export const getProviderUserId = async () => {
	const kinde = await getActiveKindeClient();
	if (kinde) return kinde.getUser()?.id;
	return (await getCurrentUser())?.uid;
};

/**
 * The email address of the signed-in account, for DISPLAY only ("You're signed in as …"), or null. Never an
 * authorisation input: the server resolves who this is from the verified token.
 */
export const getSignedInEmail = async () => {
	try {
		const kinde = await getActiveKindeClient();
		if (kinde) return kinde.getUser()?.email || null;
		return (await getCurrentUser())?.email || null;
	} catch {
		return null;
	}
};

// The Kinde SDK (is_dangerously_use_local_storage mode) persists the refresh token to this RAW
// localStorage key on every successful legacy-flow refresh (confirmed by reading the SDK source:
// useRefreshToken's success handler does `localStorage.setItem('kinde_refresh_token', ...)` when
// isUseLocalStorage is true — our getKindeToken() prefers exactly that legacy getToken() reader,
// so this key gets populated in normal use). kinde.logout() clears its OWN internal session
// storage but never touches this raw key, and the SDK's own init-time
// migrateLegacyRefreshTokenKey() resurrects it into a fresh session on the very next page load if
// it's still present — i.e. "logs out, then silently logs back in" on refresh. Not exported by the
// SDK as a constant; hardcoded from the installed @kinde-oss/kinde-auth-pkce-js source — re-verify
// against node_modules if this recurs after an SDK upgrade.
const KINDE_LEGACY_REFRESH_TOKEN_KEY = 'kinde_refresh_token';

/**
 * Provider-aware logout. Clears the platform localStorage keys, then either redirects
 * through Kinde's logout endpoint (which navigates away) or signs out of Firebase.
 */
/**
 * Drop the platform's own session keys. Split out of `logout` so a cold boot that finds a STALE
 * token can discard it without performing a sign-out it has nothing to sign out of.
 */
// Survives the logout round-trip in this tab, so the app that boots on the way back knows it is
// finishing a sign-out rather than starting a session.
const SIGNED_OUT_MARKER = 'zezamii.signedOut';

/** Read the marker and clear it — the boot after a sign-out, once. */
export const consumeSignedOutMarker = () => {
	const present = sessionStorage.getItem(SIGNED_OUT_MARKER) === '1';
	if (present) sessionStorage.removeItem(SIGNED_OUT_MARKER);
	return present;
};

// Set when the session ended ON ITS OWN rather than by choice. Both land on sign-in, and without
// this the page cannot tell them apart. Same sessionStorage round-trip rules as SIGNED_OUT_MARKER.
const SESSION_EXPIRED_MARKER = 'zezamii.sessionExpired';

/** Record that this sign-out is an expiry, not a choice. Best-effort; storage can be unavailable. */
export const markSessionExpired = () => {
	try {
		sessionStorage.setItem(SESSION_EXPIRED_MARKER, '1');
	} catch {
		/* private mode — we lose the explanation, not the sign-out */
	}
};

/** Read the expiry marker and clear it — the sign-in page after an expiry, once. */
export const consumeSessionExpiredMarker = () => {
	try {
		const present = sessionStorage.getItem(SESSION_EXPIRED_MARKER) === '1';
		if (present) sessionStorage.removeItem(SESSION_EXPIRED_MARKER);
		return present;
	} catch {
		return false;
	}
};

export const clearStoredSession = () => {
	localStorage.removeItem('id_token');
	localStorage.removeItem('od');
	localStorage.removeItem(KINDE_LEGACY_REFRESH_TOKEN_KEY);
};

/**
 * End the session.
 *
 * 🛑 `providerID` IS A SHORTCUT, NOT A REQUIREMENT. Rediscovering the provider here means
 * `getActiveKindeClient()`, whose `isAuthenticated()` call is a token round-trip — about a second of
 * "Signing out..." spent asking whether a session is live immediately before ending it. The caller
 * already recorded which door was used, so pass it and skip the probe. Omitted or unknown falls back
 * to the original discovery, so nothing depends on the hint being right.
 *
 * @param {Object} [params]
 * @param {'kinde'|'firebase'|null} [params.providerID]
 * @returns {Promise<{redirected: boolean}>} `redirected` tells the caller the page is leaving.
 */
export const logout = async ({ providerID = null } = {}) => {
	localStorage.removeItem('id_token');
	localStorage.removeItem('od');

	invalidateActiveClientCache(); // act on a fresh check, not a stale cached session

	// 🛑 THE REFRESH TOKEN IS DROPPED AT THE END, not here. The fallback below calls
	// `getActiveKindeClient()`, whose `isAuthenticated()` renews an expired access token USING this
	// key — removing it first meant that on an expired token the check answered "no session", the
	// code fell through to Firebase's signOut, and Kinde's logout endpoint was never called: the
	// SSO cookie on auth.zezamii.com outlived a sign-out that reported success.
	//
	// Still unconditional, which is what the original ordering was protecting: it runs on every
	// path, including the ones that decide there was no live session at all.
	const dropKindeRefreshToken = () => localStorage.removeItem(KINDE_LEGACY_REFRESH_TOKEN_KEY);

	// `getKindeClient` is the memoized singleton — no network, no token validation.
	if (providerID === 'kinde') {
		const kinde = await getKindeClient();
		if (kinde) {
			dropKindeRefreshToken();
			// Set BEFORE handing the page over: once the redirect starts, nothing here runs again.
			sessionStorage.setItem(SIGNED_OUT_MARKER, '1');
			await kinde.logout();
			return { redirected: true };
		}
	}

	if (providerID === 'firebase') {
		dropKindeRefreshToken();
		await signOut();
		return { redirected: false };
	}

	const kinde = await getActiveKindeClient();
	dropKindeRefreshToken();
	if (kinde) {
		sessionStorage.setItem(SIGNED_OUT_MARKER, '1');
		await kinde.logout(); // full-page redirect to logout_uri
		// The caller needs to know the page is leaving: tearing the UI down for a route we never
		// reach is what made sign-out flicker through the lazy /signin chunk.
		return { redirected: true };
	}
	await signOut();
	return { redirected: false };
};
